import { isPasswordAcceptable } from '../../auth/domain/passwordStrength';
import {
  validatePartnerApplication,
  type PartnerApplicationPayload,
} from '../../../domain/partners/application';

export type PartnerPublicRegistrationInput = {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  cnpj?: string | null;
};

export type PartnerPublicRegistrationPayload = {
  full_name: string;
  email: string;
  phone: string;
  password: string;
  application: PartnerApplicationPayload;
};

export type PartnerPublicRegistrationField =
  | 'fullName'
  | 'email'
  | 'phone'
  | 'password'
  | 'confirmPassword'
  | 'companyName'
  | 'cnpj';

export type PartnerPublicRegistrationValidation =
  | { ok: true; payload: PartnerPublicRegistrationPayload }
  | { ok: false; field: PartnerPublicRegistrationField; message: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates public partner signup (account + agency).
 * Privileged partner fields stay fixed inside validatePartnerApplication.
 */
export function validatePartnerPublicRegistration(
  input: PartnerPublicRegistrationInput,
): PartnerPublicRegistrationValidation {
  const fullName = input.fullName.trim();
  if (fullName.length < 2) {
    return {
      ok: false,
      field: 'fullName',
      message: 'Informe seu nome completo.',
    };
  }

  const email = input.email.trim().toLowerCase();
  if (!EMAIL_RE.test(email)) {
    return {
      ok: false,
      field: 'email',
      message: 'Informe um e-mail válido.',
    };
  }

  const phoneDigits = input.phone.replace(/\D/g, '');
  if (phoneDigits.length < 12) {
    return {
      ok: false,
      field: 'phone',
      message: 'Informe um telefone WhatsApp válido com DDD.',
    };
  }

  if (!isPasswordAcceptable(input.password)) {
    return {
      ok: false,
      field: 'password',
      message:
        'Use uma senha mais forte: pelo menos 8 caracteres, misturando maiúsculas, minúsculas, números e símbolos.',
    };
  }

  if (input.password !== input.confirmPassword) {
    return {
      ok: false,
      field: 'confirmPassword',
      message: 'As senhas não coincidem.',
    };
  }

  const application = validatePartnerApplication({
    companyName: input.companyName,
    cnpj: input.cnpj,
  });
  if (application.ok === false) {
    return {
      ok: false,
      field: application.field,
      message: application.message,
    };
  }

  return {
    ok: true,
    payload: {
      full_name: fullName,
      email,
      phone: input.phone.trim(),
      password: input.password,
      application: application.payload,
    },
  };
}
